import { Location } from "@cnx/common";
import { ShipmentFilters } from "types/Shipment";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { guardedLocalStorage } from "../utils/persistentStorage";

interface StopFilters {
  completed: boolean;
}

export interface ShippingManagementStore {
  resourceCombinationId: string;
  location: Location | null;
  shipmentFilters: ShipmentFilters;
  stopFilters: StopFilters;
  setLocation: (location: Location) => void;
  setStopFilters: (stopFilters: StopFilters) => void;
  setShipmentFilters: (shipmentFilters: ShipmentFilters) => void;
  setResourceCombinationId: (resourceCombinationId: string) => void;
  reset: () => void;
  resetShipmentFilters: () => void;
  resetOneShipmentFilter: (filterKey: keyof ShipmentFilters) => void;
}

const initialShipmentFilters = {
  driverQuery: "",
  trailerQuery: "",
  tripIdQuery: "",
  truckQuery: "",
};

const initialState: ShippingManagementStore = {
  resourceCombinationId: "",
  location: null,
  shipmentFilters: initialShipmentFilters,
  stopFilters: {
    completed: false,
  },
  setLocation: () => {},
  setStopFilters: () => {},
  setShipmentFilters: () => {},
  resetShipmentFilters: () => {},
  setResourceCombinationId: () => {},
  reset: () => {},
  resetOneShipmentFilter: () => {},
};

export const useShippingManagementStore = create<ShippingManagementStore>()(
  persist(
    set => ({
      location: initialState.location,
      shipmentFilters: initialState.shipmentFilters,
      stopFilters: initialState.stopFilters,
      resourceCombinationId: initialState.resourceCombinationId,
      setLocation: (location: Location) => set({ location }),
      setStopFilters: (stopFilters: StopFilters) => set({ stopFilters }),
      setShipmentFilters: (shipmentFilters: ShipmentFilters) =>
        set({ shipmentFilters }),
      setResourceCombinationId: (resourceCombinationId: string) =>
        set({ resourceCombinationId }),
      resetShipmentFilters: () => {
        set({
          shipmentFilters: initialState.shipmentFilters,
        });
      },
      resetOneShipmentFilter: (filterKey: keyof ShipmentFilters) => {
        set(state => ({
          ...state,
          shipmentFilters: {
            ...state.shipmentFilters,
            [filterKey]: initialState.shipmentFilters[filterKey],
          },
        }));
      },
      reset: () => {
        set({
          shipmentFilters: initialState.shipmentFilters,
          stopFilters: initialState.stopFilters,
        });
      },
    }),
    {
      name: "ShippingManagementStore",
      storage: createJSONStorage(() => guardedLocalStorage),
      partialize: state => ({
        resourceCombinationId: state.resourceCombinationId,
        location: state.location,
        shipmentFilters: state.shipmentFilters,
        stopFilters: state.stopFilters,
      }),
    }
  )
);
